import { Empty, Flex, FormInstance } from "antd";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Point, Schedule } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { ScheduleFiltersPanel, ScheduleMenuConfig } from "@/domains/route";
import { useAppSelector } from "@/hooks";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { BlockLoader } from "@/shared/ui/BlockLoader";

import RouteEditorForm, {
    RouteEditorFormValues,
} from "../../components/RouteEditorForm";
import { ScheduleList } from "../../components/ScheduleList";
import { useScheduleSelection } from "../../hooks";
import {
    RouteEditorModal,
    RouteEditorModalState,
} from "../../RouteEditor.types";

export type RouteEditorCreateMainContentBlockProps = {
    routeEditorForm: FormInstance<RouteEditorFormValues>;
    schedulesLoading: boolean;
    points: Record<string, Point>;
    saveLoading: boolean;
    selection: ReturnType<typeof useScheduleSelection>;
    onSave: () => void;
    onSetModalState: (state: RouteEditorModalState) => void;
};

export const RouteEditorCreateMainContentBlock = ({
    routeEditorForm,
    schedulesLoading,
    points,
    saveLoading,
    selection,
    onSave,
    onSetModalState,
}: RouteEditorCreateMainContentBlockProps) => {
    const navigate = useNavigate();

    const currentScheduleList = useAppSelector(
        (s) => s.manager.currentScheduleList,
    );

    const handleAddPointClick = useCallback(() => {
        navigate(ROUTES.RouteManagementCreateAddPoint, {
            state: { formValues: routeEditorForm.getFieldsValue() },
        });
    }, [navigate, routeEditorForm]);

    const scheduleMenuConfig: ScheduleMenuConfig[] = useMemo(
        () => [
            {
                type: "view",
                label: "Переглянути ТТ",
                onClick: (item: Schedule) => {
                    navigate(
                        buildRoutePath(ROUTES.PointDetails, {
                            pointId: item.pointId,
                        }),
                    );
                },
            },
            {
                type: "edit",
                label: "Редагувати",
                onClick: (item: Schedule) => {
                    onSetModalState({
                        type: RouteEditorModal.EditSchedule,
                        payload: {
                            schedule: item,
                            createMode: true,
                        },
                    });
                },
            },
            {
                type: "delete",
                label: "Видалити",
                onClick: (item: Schedule) => {
                    onSetModalState({
                        type: RouteEditorModal.DeleteSchedule,
                        payload: {
                            pointIds: [item.pointId],
                            isEmptyAfterDelete:
                                currentScheduleList.length === 1,
                            createMode: true,
                        },
                    });
                },
            },
        ],
        [currentScheduleList.length, navigate, onSetModalState],
    );

    return (
        <MainContainer $paddingTop="0" $paddingBottom="8px">
            <Flex
                vertical
                gap={16}
                style={{
                    overflowY: "auto",
                    paddingTop: 16,
                    height: "100%",
                }}
            >
                {!selection.multiSelectMode && (
                    <RouteEditorForm form={routeEditorForm} />
                )}

                <ScheduleFiltersPanel form={selection.filtersForm} />

                {schedulesLoading ? (
                    <BlockLoader />
                ) : selection.sortedAndFilteredSchedules?.length ? (
                    <ScheduleList
                        displaySchedules={selection.sortedAndFilteredSchedules}
                        multiSelectMode={selection.multiSelectMode}
                        selectedSchedules={selection.selectedSchedules}
                        handleSelectSchedule={selection.handleSelectSchedule}
                        handleTap={selection.enterMultiSelectMode}
                        dayFilter={selection.watchedFilters?.day ?? ""}
                        scheduleMenuConfig={scheduleMenuConfig}
                        points={points}
                    />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Немає активних точок"
                    />
                )}
            </Flex>

            <Flex vertical gap={8} style={{ paddingTop: 8, marginTop: "auto" }}>
                <BaseButton
                    type="default"
                    onClick={handleAddPointClick}
                    disabled={saveLoading}
                >
                    Додати ТТ
                </BaseButton>

                <BaseButton
                    type="primary"
                    onClick={onSave}
                    loading={saveLoading}
                >
                    Зберегти
                </BaseButton>
            </Flex>
        </MainContainer>
    );
};
